/**
 * The runs surface: a ranged, paged table of one sync's runs, with the open run's detail beside it.
 *
 * Two things here are product rules rather than layout. Run-log RETENTION is stated exactly once in
 * the whole product — it used to be stated here, as a sentence beside the range picker, and Figma
 * `334:514` moves it into the run detail's `CONNECTION` band
 * (`ConnectionSettingsBand`), where it sits with the other setting that governs every run rather than
 * as loose prose in a toolbar. "Exactly once" is the part that must not rot: do not reinstate a
 * sentence here, and do not drop the band's `null` case (retention OFF), which is the whole reason the
 * old sentence had two forms. And the `Changed → Accepted` column exists because the two-sided pairing
 * is the signal: an audience adding thousands and removing nobody has broken quietly, and that has to
 * be visible in the list, not only after opening a run.
 *
 * The table and the detail COEXIST — selecting a row must never displace the list it was selected
 * from. That used to be exactly backwards: this panel returned the detail card INSTEAD of the table,
 * so opening a run destroyed the very list it was opened from, and a long failure string pushed the
 * run history below the fold at the moment it was most diagnostic. Fixed by a split rather than a
 * toggle: a FLUID table beside a `w-[23rem]` detail (Band B's 368px). At the page's 71rem cap the table
 * comes out at Band B's 744px; below it the table gives up width. In a pane too narrow for both, and
 * whenever the failure chat is open, the open run shows as a `RunStrip` ABOVE the table instead — one
 * stacked layout for both cases, decided in one place (`runDetailLayout`), see `RUNS_GRID`.
 *
 * Ported from `syncDetailsPage/components/pastRuns/index.tsx`, whose table had no headers and no
 * delta column.
 */
import { ANALYTICS_EVENTS, type AnalyticsEventProperties } from '@server/analytics-events';
import { ACTIVATION_HANDOFF_QUESTION_MAX_CHARS } from '@server/app-urls';
import { History, MessageSquareText } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

import { ListEmpty, ListError, ListSkeleton } from '@/_shared/components/list/ListStates';
import { Table, type TableColumn } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Card } from '@/_shared/components/ui/card';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useIsMobile } from '@/_shared/hooks/use-mobile';
import { trackClientEvent } from '@/_shared/lib/analytics';
import { formatDateTimeTitle } from '@/_shared/lib/format';
import { cn } from '@/_shared/lib/utils';

import { usePastRuns } from '../hooks/usePastRuns';
import { useRunSelection } from '../hooks/useRunSelection';
import {
  deliveryOutcome,
  formatDurationSeconds,
  group,
  isCounted,
  isRunning,
  RUN_RETENTION_DAYS,
  runDurationSeconds,
  runHasFailures,
  runStatus,
  runSummary,
} from '../lib/run-view';
import { useTickingNow } from '../lib/use-ticking-now';
import type { IdentifierMapping, RawSyncRun } from '../types';
import type { ActivationChatLinkModel, ActivationFailureChatDrawer } from './ActivationFailureChatDrawer';
import { PastRunsPagination } from './PastRunsPagination';
import { PastRunsRangePicker } from './PastRunsRangePicker';
import { RunDenominated } from './RunDenominated';
import { runStartedLabel, runTypeLabel } from './RunLabels';
import { RunOverview } from './RunOverview';
import { RunStrip } from './RunStrip';
import { SyncStatusBadge } from './SyncStatusBadge';

type ChatDrawer = typeof ActivationFailureChatDrawer;
type ActivationChatOpenSource = AnalyticsEventProperties['activationChatOpened']['source'];

interface RunsPanelProps {
  syncId: string;
  /** The far-side audience by name where known, else its id, else the account — see the ladder. */
  /** Registry key for the destination's brand mark, e.g. `FB_CUSTOM_AUDIENCE`. */
  destinationDefinitionName?: string | undefined;
  /** `RawSqlAudience.sourceDefinitionName` — the warehouse mark on the open run's Extract row. */
  sourceDefinitionName?: string | undefined;
  /**
   * The sync has a run in flight per the header's poll — keep looking even if none is listed yet.
   * Deliberately WIDE: a cron-fired run is a run this table must DISCOVER. It drives the poll and
   * nothing else; what may move the reader's selection is `userStartedRun` below.
   */
  live?: boolean;
  /**
   * THIS reader pressed Run sync and the run they asked for has not settled yet
   * (`useRunSync().userStartedRun`). The one thing that licenses Rule 2 below to move the selection.
   *
   * It is NOT `live`. `live` is true for a cron fire too (`ActivationDetail` derives it from
   * `runSync.state !== 'idle'`, and `nextRunButtonState` returns `'running'` for an externally
   * triggered run), so gating the follow on it meant a scheduled sync firing while someone studied
   * an old failed run pulled the pane off it — silently, and via `replace`, so Back could not undo
   * it. Following a run is a reader's own request being answered; nothing else earns it.
   */
  userStartedRun?: boolean;
  /**
   * Whether this sync has EVER STARTED a run — `sync.lastRun !== undefined` (`RawSync.lastRun`,
   * `types.ts`), computed by the caller, which is the one that already holds the `RawSync`. Started,
   * not completed: `lastRun` is a whole `RawSyncRun`, which may be running, failed or succeeded, and
   * carries no completion semantics at all. Nothing below needs it to — every use is "is there any
   * run to show", where a run in flight counts exactly as much as a finished one. This is
   * NOT derivable from anything `usePastRuns` fetches: that query is windowed (bounded by the range
   * picker and by retention), so an empty page from it means "no run FALLS IN THIS WINDOW" — it
   * cannot distinguish a sync that has never run from one whose every run aged out of the window. Only
   * the platform's own `lastRun`, which carries no window at all, can tell the two apart. Do NOT add a
   * network call to answer this here — the fact is already sitting in `ActivationDetail`'s `detail.sync`.
   *
   * Gates the two-pane body below TOGETHER WITH the loaded rows — never on its own. When this is
   * `false` AND no rows have arrived, the table/detail split does not render: there is no row to head
   * and no run to select, so a table frame and a detail column both
   * showing "No runs yet" side by side is two renderings of the same fact, neither of them centred on
   * the page. Instead this renders ONE `ListEmpty` filling the content region, with the "Sync runs"
   * toolbar (title + range picker + refresh) left standing above it: the range picker is what decides
   * whether runs appear at all, so a reader who thinks a narrow range is hiding their runs still has
   * the control that would prove it. When `true`, the two-pane split renders and this prop plays no
   * further part — the table's own `emptyState` and the detail column's own fallback each cover the
   * "ran before, nothing in THIS window" case on their own terms.
   */
  hasEverRun: boolean;
  /**
   * This connection's own retention — `retentionDays(sync)` (`lib/run-view.ts`), computed by the
   * caller, which is the one that already holds the `RawSync`. REQUIRED, not defaulted:
   * `retentionDays(sync)` is the ONE place a missing value legitimately becomes the platform default
   * — every caller below that point must pass the already-resolved value, so a future caller cannot
   * forget it and silently state (and query) 30 days regardless of what the connection actually keeps.
   *
   * `null` is a real, distinct value here — not "forgot to pass it" — meaning the connection has
   * explicitly turned run-log retention OFF. The window/calendar machinery below still needs an
   * actual number (there is no "disabled" query shape), so `RUN_RETENTION_DAYS` is what it queries in
   * that case; the SENTENCE renders the true "not retained" state rather than a number nobody backs.
   */
  retentionDays: number | null;
  /**
   * `sync.config.destination.syncMode` — CONNECTION-scoped, threaded through to the run detail's
   * settings band. It is NOT a run property and nothing in the table may key off it; see
   * `ConnectionSettingsBand` for why it is on this page at all.
   */
  syncMode?: string | undefined;
  /**
   * `sync.config.destination.identifierMappings` — CONNECTION-scoped, threaded through to the run
   * detail's settings band alongside `syncMode`, and equally not a run property. `undefined` means the
   * sync has not arrived; see `ConnectionSettingsBand` for the three states.
   */
  identifierMappings?: IdentifierMapping[] | undefined;
  /** `sync.config.destination.fieldMappings` — LinkedIn's or Gainsight PX's second table; see the band's doc. */
  fieldMappings?: IdentifierMapping[] | undefined;
  /** The second table's name for this destination; see the band's doc. */
  fieldMappingsLabel?: string | undefined;
  /** Opens the sync's edit sheet, from the connection band's `Edit`. Owned by `ActivationDetail`. */
  onEditConnection?: (() => void) | undefined;
  /** Why `Edit` on the connection band is unavailable. Threaded through — see the band's own prop. */
  editConnectionDisabledReason?: string | undefined;
  /**
   * The activation failure chat exists for this page (`ActivationDetail` decides: flag on, not demo,
   * a Lookout activation row; sibling workspaces included). False renders no `Ask why` and no drawer.
   */
  chatEnabled?: boolean;
  /** `activationChat.availability`'s answer for the drawer's read-only note; `null` while unknown. */
  perRecordReads?: boolean | null;
  /** `activationChat.availability`'s link model: picks the sibling note over the read-only one. */
  linkModel?: ActivationChatLinkModel | null;
  /** `activationChat.availability` failed: `activationChatOpened` stops waiting for its segments. */
  chatAvailabilityFailed?: boolean;
  /** The drawer's activation chip ("destination name · destination audience"). */
  activationLabel?: string | null;
  /** The connection's current "Retain and show failures": days, `null` off, `undefined` unknown. */
  failuresRetentionDays?: number | null;
  /**
   * The page's chat pane (`ActivationDetail`'s right-hand split panel), which the chat renders into
   * through a portal. The page shows that pane only while {@link onChatShownChange} says the chat is
   * open, so this is `null` while closed and for the commit that opens it.
   */
  chatPane?: HTMLElement | null;
  /** Tells the page whether to show the chat pane. Called from a layout effect, so the pane is in before paint. */
  onChatShownChange?: (shown: boolean) => void;
}

/**
 * The right column's loading treatment — case (a) of review IMPORTANT 2. Shaped like the two cards
 * `RunOverview` actually renders — the compact identity card above, the taller phase stack below — so
 * it belongs to the same family rather than reading as a different kind of placeholder next to it.
 * Mirrors the LEFT column's own gate (`pastRuns.isLoading`) rather than a separate one — the two
 * columns are loading the same fetch.
 */
/**
 * The chat chip's failed count for the selected run. Null (the chip names the run alone) when there is
 * no count to show: no run, no `failed` metric or a failed run that counted nothing (it stopped before
 * counting — "0 failed" would contradict its Failed badge), or a run still running, whose count is still
 * moving.
 */
function selectedRunFailedCount(run: RawSyncRun | undefined | null): number | null {
  if (!run || isRunning(run)) return null;
  // rudder-sources zeroes every metric of a run that failed before counting (rather than leaving them
  // out); the table says "Stopped before any records were counted.", so the chip names no count either.
  const status = runStatus(run);
  if ((status === 'failed' || status === 'unknown') && (run.metrics?.total ?? 0) === 0) return null;
  return run.metrics?.failed?.total ?? null;
}

function RunDetailSkeleton(): React.ReactElement {
  return (
    <div role="status" aria-label="Loading run detail">
      {/* `Card`, like the two real cards these stand in for (`RunIdentityCard` and `RunOverview`'s
          phase stack). A skeleton that hand-rolls the surface it is imitating drifts the moment the
          primitive changes, and the drift shows up as a flicker on every load — the one moment
          nobody is looking closely enough to catch it. */}
      <Card className="mb-4 gap-2 p-4">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-full" />
      </Card>
      <Card className="gap-4 p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </Card>
    </div>
  );
}

/**
 * The duration cell — and the only thing on this surface that owns a ticking clock.
 *
 * The clock lives HERE, not in `RunsPanel`, and that is a rendering-correctness requirement rather
 * than tidiness. `Table` memoises its `columnDefs` on `[columns, onRowClick]` and renders each cell
 * through `flexRender`, which does `createElement(cellFn, …)` — so a cell function whose identity
 * changes makes React see a new element TYPE and unmount/remount the whole cell subtree. A `now`
 * read in `RunsPanel` is closed over by this column's `cell`, which rebuilds the columns array,
 * which rebuilds every columnDef: at one tick a second, while any listed run is live, EVERY cell in
 * the table remounted — including the row activator `<button>`, so a keyboard user could not hold
 * focus on a row while a run was running, on the page whose whole promise is that any row is
 * selectable. Subscribing to the clock inside this leaf keeps the columns array constant, and only
 * the running row's own duration re-renders.
 *
 * `isRunning(run)` alone, per the hook's documented API: a settled run's duration comes from its own
 * two timestamps and needs no clock. (A settled run with no `finished_at` — which the platform does
 * not send — now freezes at this cell's mount instead of drifting with an unrelated live run's tick.)
 */
function RunDurationCell({ run }: { run: RawSyncRun }): React.ReactElement {
  const now = useTickingNow(isRunning(run));
  return <span className="text-xs text-muted-foreground">{formatDurationSeconds(runDurationSeconds(run, now))}</span>;
}

/** The chat's heading (`ActivationFailureChatDrawer`). A string here: importing that module would load it eagerly. */
const ACTIVATION_CHAT_TITLE_ID = 'activation-failure-chat-title';

/** The hand-off link's request to open the failure chat on load. */
const ASK_PARAM = 'ask';
/** The hand-off link's question, put in the composer for the person to send. It is never sent on its own. */
const QUESTION_PARAM = 'q';

/**
 * The runs pane's width, in rem, from which the run detail column fits BESIDE the table: the 23rem
 * column, the `gap-x-6` and a table of about 23rem. At the page's 71rem cap that table is Band B's
 * 744px; between the two the table shrinks and the detail keeps its width.
 */
const DETAIL_BESIDE_MIN_REM = 48;

/**
 * Where the open run's detail goes — the ONE rule for it, so the two layouts cannot drift apart:
 *
 *   · `beside` — the vertical run detail column (`RunOverview`) to the right of the table. Only while
 *     the chat is closed AND the runs pane is at least {@link DETAIL_BESIDE_MIN_REM} wide.
 *   · `strip` — the horizontal `RunStrip` in a row of its own ABOVE the toolbar and table, and the
 *     detail column hidden. Whenever the chat is open (the chat stands in for the column), and whenever
 *     the pane is too narrow for the column (a narrow window, a phone). Before this, a narrow pane
 *     stacked the vertical column UNDER the table, which on a phone put the run a long scroll below the
 *     list it was opened from, and laid the page out differently from the same pane with the chat open.
 *
 * Measured against `RunsPanel`'s own width, not the viewport: the failure chat's split hands this panel
 * anything from the whole page down to 38% of it.
 */
type RunDetailLayout = 'beside' | 'strip';

function runDetailLayout(chatShown: boolean, paneFitsDetail: boolean): RunDetailLayout {
  return !chatShown && paneFitsDetail ? 'beside' : 'strip';
}

/**
 * Whether the element can take focus where it stands: connected AND rendered. The run detail column is
 * `hidden` (not unmounted) whenever the strip stands in for it, so `isConnected` alone is not enough.
 * A DOM without `checkVisibility` (jsdom, which lays nothing out) counts a connected element as shown.
 */
function isShown(el: HTMLElement | null | undefined): el is HTMLElement {
  if (!el?.isConnected) return false;
  return typeof el.checkVisibility === 'function' ? el.checkVisibility() : true;
}

/**
 * Whether the element is at least {@link DETAIL_BESIDE_MIN_REM} wide, re-measured on every resize. An
 * element that has no width to measure (not laid out, or jsdom) counts as wide: the page's desktop
 * layout is the default.
 *
 * For the one resize that follows a close (the chat pane leaving widens the runs pane), the resize path
 * commits with `flushSync`, and that is what keeps the swap to one frame. A `ResizeObserver` callback runs after
 * layout and before paint, but a plain `setState` there is NOT rendered before paint: React gives it a
 * default-priority lane and renders it in a later task. Closing the chat at 1440px (the runs pane at 62%,
 * under 48rem) committed the `strip` layout, the pane widened to the whole body, and one frame painted the
 * strip over a full-width table before the column came back (Aris on #1030, measured frame by frame in
 * `ActivationDetail.browser.test.tsx`). `flushSync` renders the new layout inside the callback, so the
 * browser lays it out again and paints only that.
 *
 * Not a CSS container query, though that resolves in the same layout pass with no JS: it needs the strip
 * and the column both mounted with one hidden, and the hidden one's measuring hooks (the error's clamp,
 * the mapping preview's cut, the stage blocks' fit) then measure a `display: none` box as zero and fix
 * themselves one frame after it shows, which moved the stale frame inside the strip rather than removing
 * it (measured: the long error's `Show more` missing for one frame when the chat opens at 1600px).
 */
function usePaneFitsDetail(ref: React.RefObject<HTMLElement | null>, decidesLayout: boolean): boolean {
  const [fits, setFits] = useState(true);
  // Read by the observer below: the committed answer, so it updates only on a real change, and whether
  // the chat has just closed, so the resize that follows (the chat pane leaving) is the one to flush.
  const fitsRef = useRef(fits);
  const decidesLayoutRef = useRef(decidesLayout);
  const flushNextWidenRef = useRef(false);
  // A layout effect, so all three are current by the time the next frame's observer runs.
  useLayoutEffect(() => {
    fitsRef.current = fits;
    if (decidesLayout !== decidesLayoutRef.current) {
      decidesLayoutRef.current = decidesLayout;
      // The chat just closed (`true`) or opened (`false`): the pane's next width change is its pane
      // leaving or arriving. Only the close's widening is flushed; see the observer.
      flushNextWidenRef.current = decidesLayout;
    }
  });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): boolean | null => {
      const width = el.getBoundingClientRect().width;
      if (width === 0) return null;
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return width >= DETAIL_BESIDE_MIN_REM * rem;
    };
    // Inside this layout effect React is already committing: a plain update lands before paint.
    const first = measure();
    if (first !== null) setFits(first);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      // The first notification after a close is the chat pane leaving (its width on the desktop, its
      // height on a phone, where the chat stacks under the runs). It consumes the flag whether or not the
      // answer changes: left set past a close that changes nothing (a phone close, a close at 1600px), it
      // would flush a later, unrelated widening.
      const afterClose = flushNextWidenRef.current;
      flushNextWidenRef.current = false;
      const next = measure();
      if (next === null || next === fitsRef.current) return;
      fitsRef.current = next;
      // Flushed only for that one resize, and only towards `beside`: the chat pane leaving widens the runs
      // pane, and that swap is the frame the reporter saw. Every other change is a plain update, as before
      // this hook flushed at all. Flushing other widenings is not safe: a page opened at phone width and
      // widened to the desktop raised "ResizeObserver loop completed with undelivered notifications" on
      // window (3 of 3 on mini at 390 -> 1440 with the sidebar, 0 of 3 on main). The flushed layout and
      // the sidebar column that mounts on the breakpoint flip then resize boxes shallower than ones the
      // browser already delivered in that frame. Narrowing was never flushed; the breakpoint's own update
      // (`useIsMobile`) is not involved: its media-query listener runs at discrete priority and, under a
      // resize, a frame after the observers.
      if (afterClose && next) flushSync(() => setFits(next));
      else setFits(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return fits;
}

/**
 * The runs body: the toolbar on top, the table under it and the run detail beside it or above it (see
 * {@link runDetailLayout}) — a grid rather than a row of fixed columns, so the table is FLUID.
 *
 *   · `beside`: two columns (`minmax(0,1fr)` table + 23rem detail). The toolbar is pinned to the
 *     table's column so it reads as the table's own toolbar, and the table and the detail share one top
 *     edge (row 2) without a hardcoded margin.
 *   · `strip` with the chat open: one column held to the pane's height, the strip, the toolbar and the
 *     table, which scrolls inside itself — the funnel, status and connection settings stay on screen
 *     beside the chat. The table's row has a 17rem floor, its header and three run rows at the runs'
 *     narrowest (measured at 1366×650, the runs at 38%: header 49px, rows 57 to 75px as the delta
 *     wraps), so a tall strip (a long error, expanded) makes the PANE scroll instead of squeezing the
 *     table to nothing.
 *   · `strip` with the chat closed (a narrow pane, a phone): the same rows, but the grid takes its
 *     CONTENT's height (`flex-none`) and the page scrolls: held to the pane's height, the table would be
 *     squeezed to a row or two under a strip that, on a phone, is most of a screen tall.
 *   · No run open (or none loaded yet): no strip row, the toolbar and table at the top.
 */
const RUNS_GRID = {
  base: 'grid min-h-0 grid-cols-1 content-start gap-x-6 gap-y-3',
  beside: 'flex-1 grid-cols-[minmax(0,1fr)_23rem] grid-rows-[auto_minmax(0,1fr)]',
  withStrip: 'flex-1 grid-rows-[auto_auto_minmax(17rem,1fr)]',
  withStripPageScroll: 'flex-none grid-rows-[auto_auto_auto]',
  withoutDetail: 'flex-1 grid-rows-[auto_minmax(0,1fr)]',
} as const;

/**
 * The two numeric columns' widths, by the TABLE column's width (that column is the `@container`; these
 * custom properties sit one level in, because a container query never matches its own element). The run cell
 * takes what is left and truncates. Wide (46rem and up, which includes Band B's 744px) keeps the widths
 * this table always had; the steps below give the run cell room at a narrow split and on a phone, where
 * the delta wraps to two lines instead of truncating to nothing, and so may a header (the table's
 * `[&_th]:whitespace-normal`: "Changed → Accepted" over a 7rem column), and the cells pad 0.5rem
 * rather than 0.75rem there. Measured at 1280×800 with the sidebar expanded and the chat at its widest
 * (the runs at their 38% minimum) and at 390px: in that step the status badge may wrap inside itself.
 */
const RUN_COLUMN_WIDTHS =
  '[--runs-col-delta:7rem] [--runs-col-duration:4.25rem] @lg:[--runs-col-delta:12rem] @lg:[--runs-col-duration:6rem] @min-[46rem]:[--runs-col-delta:15rem] @min-[46rem]:[--runs-col-duration:7rem]';

export function RunsPanel({
  syncId,
  destinationDefinitionName,
  sourceDefinitionName,
  live = false,
  userStartedRun = false,
  hasEverRun,
  retentionDays,
  syncMode,
  identifierMappings,
  fieldMappings,
  fieldMappingsLabel,
  onEditConnection,
  editConnectionDisabledReason,
  chatEnabled = false,
  perRecordReads = null,
  linkModel = null,
  chatAvailabilityFailed = false,
  activationLabel = null,
  failuresRetentionDays,
  chatPane = null,
  onChatShownChange,
}: RunsPanelProps): React.ReactElement {
  const [chatOpen, setChatOpen] = useState(false);
  // The drawer's module loads once the chat is on for this page, so it stays out of the initial bundle
  // (`lookout-bundle-budget`). Not React.lazy: a Suspense mount runs the composer's effects before
  // TipTap has mounted its view.
  const [ChatDrawer, setChatDrawer] = useState<ChatDrawer | null>(null);
  useEffect(() => {
    if (!chatEnabled || ChatDrawer) return;
    let live = true;
    void import('./ActivationFailureChatDrawer').then((m) => {
      if (live) setChatDrawer(() => m.ActivationFailureChatDrawer);
    });
    return () => {
      live = false;
    };
  }, [chatEnabled, ChatDrawer]);
  const drawerShown = chatEnabled && chatOpen && ChatDrawer !== null;
  // The page owns the pane the chat sits in (beside the runs, below the header — see
  // `ActivationFailureChatDrawer`'s doc), this panel owns the chat. A layout effect, so the page's pane
  // and the chat inside it land before the browser paints: no frame with an empty pane. The cleanup
  // closes the pane on close AND on unmount (a different sync remounts this panel by key).
  useLayoutEffect(() => {
    if (!drawerShown || !onChatShownChange) return;
    onChatShownChange(true);
    return () => onChatShownChange(false);
  }, [drawerShown, onChatShownChange]);
  // The control that opened the drawer (`Ask why`, or `Ask about failures`), so closing hands focus
  // back to it instead of dropping it on <body>.
  const chatOpenerRef = useRef<HTMLElement | null>(null);
  // One object per open: `activationChatOpened` fires once for each, whatever re-renders follow.
  const [chatOpening, setChatOpening] = useState<{ source: ActivationChatOpenSource } | null>(null);
  const openChat = useCallback(
    (source: ActivationChatOpenSource) => {
      // Only ever one open: a second click that lands before the first has committed changes nothing.
      if (chatOpen) return;
      chatOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setChatOpen(true);
      setChatOpening({ source });
    },
    [chatOpen],
  );
  const askWhy = useCallback(() => openChat('ask_why'), [openChat]);
  // When the open chat opened; `null` while none is open, so a close is only ever counted once, after its open.
  const chatOpenedAt = useRef<number | null>(null);
  const trackedOpening = useRef<{ source: ActivationChatOpenSource } | null>(null);
  const openingRun = useRef<{
    opening: typeof chatOpening;
    linkedRunId: string | null;
    known: boolean;
    runFailed?: boolean;
  } | null>(null);
  // Set below, once `selected` and the rest exist. Sends the current opening's event if it has not gone yet.
  const sendChatOpened = useRef<() => void>(() => undefined);
  // Ends the open, for the funnel: the open goes first if it was still waiting for its segments, so a close never comes
  // before or without its open. From `closeChat`, and from leaving the page inside the app.
  const trackChatClosed = useCallback(() => {
    if (chatOpenedAt.current === null) return;
    sendChatOpened.current();
    trackClientEvent(ANALYTICS_EVENTS.activationChatClosed, {
      rsConnectionId: syncId,
      elapsedMs: Date.now() - chatOpenedAt.current,
    });
    chatOpenedAt.current = null;
  }, [syncId]);
  // Set by `closeChat`, consumed once the close has committed: the opener sits in the run detail, which
  // is hidden while the chat shows, and a hidden element cannot take focus.
  // `close`: the close has not committed yet. `follow`: focus went to the strip's `Ask why` because the
  // opener was hidden, and follows the next layout flip (below).
  const focusOpenerOnClose = useRef<'close' | 'follow' | null>(null);
  const closeChat = useCallback(() => {
    focusOpenerOnClose.current = 'close';
    setChatOpen(false);
    trackChatClosed();
  }, [trackChatClosed]);
  // Leaving inside the app while the chat is open ends it too (a different sync remounts this panel by key). A browser
  // leave sends nothing. The microtask lets StrictMode's simulated unmount, which remounts at once, count for nothing.
  const panelMounted = useRef(false);
  const trackChatClosedRef = useRef(trackChatClosed);
  useEffect(() => {
    trackChatClosedRef.current = trackChatClosed;
  });
  useEffect(() => {
    panelMounted.current = true;
    return () => {
      panelMounted.current = false;
      queueMicrotask(() => {
        if (!panelMounted.current) trackChatClosedRef.current();
      });
    };
  }, []);
  // The window/calendar machinery needs a real positive number even when logs are not retained at
  // all — there is no separate "disabled" query shape, and a query still has to ask for SOMETHING.
  // The sentence below renders the true "not retained" state off the untouched `retentionDays` prop,
  // not off this fallback.
  const windowDays = retentionDays ?? RUN_RETENTION_DAYS;
  const pastRuns = usePastRuns(syncId, live, windowDays);

  /**
   * Refetch once the run you were watching settles, instead of waiting for a clock.
   *
   * The cadences alone cannot cover this. `live` is `state !== 'idle'`, and for a run that changes
   * nothing the whole lifecycle — start, extract, diff, finish — lasts about four seconds. The live
   * cadence is ten. A poll interval can never fire inside a window shorter than itself, so the row
   * fell through to the settled tick: measured 63 SECONDS between pressing Run sync and the run
   * appearing, with the button back at rest the whole time and nothing on screen to say the press had
   * done anything.
   *
   * The page already KNOWS the run finished — that is exactly what moved `live` back to false — so the
   * fix is to say so rather than to guess a faster interval. Event-driven, one refetch per settle, and
   * it leaves both cadences alone: a slow poll is the right behaviour for a page nobody is acting on.
   */
  const wasLive = useRef(false);
  useEffect(() => {
    if (wasLive.current && !live) pastRuns.refetch();
    wasLive.current = live;
  }, [live, pastRuns]);
  // Selection holds an ID, not the row object: a run selected while it is still live must keep
  // receiving the poll's newer metrics, and a snapshot would freeze it mid-flight forever. It lives
  // in the URL (`useRunSelection`), not local state, so a run is linkable and the page/machine choice
  // (default, follow) is distinguishable in history from a user's own click — see that hook's doc.
  const { selectedRunId, select, selectSilently } = useRunSelection();
  const { runs } = pastRuns;
  const selected = runs.find((run) => run.run_id === selectedRunId);
  const panelRef = useRef<HTMLDivElement>(null);
  const layout = runDetailLayout(drawerShown, usePaneFitsDetail(panelRef, !drawerShown));
  // The strip stands in for the hidden detail column, for the run that column would show. No run
  // selected (or none loaded yet): no strip, and the table sits at the top.
  // Not on a phone WHILE THE CHAT IS OPEN: there the page stacks the chat UNDER the runs
  // (`ActivationDetail` splits on the same `useIsMobile`), the runs keep a band of about 150px, and a
  // strip (several hundred px tall at phone width) would fill it and push the table out of reach. The
  // chat's own context chips name the run and its failed count there. With the chat closed a phone shows
  // the strip like any narrow pane.
  const isMobile = useIsMobile();
  const showStrip = layout === 'strip' && selected !== undefined && !(drawerShown && isMobile);
  // Runs on the close AND on each layout flip after it, because the close can land in the strip layout
  // and flip to `beside` in a second commit. The runs pane is still at its chat-open width when the close
  // commits, and only the pane's `ResizeObserver` sees it widen (before that frame paints, see
  // `usePaneFitsDetail`). Opened from the column beside the table
  // and closed with a narrow pane, the opener is still connected but hidden (`display: none`), and
  // focusing it drops focus on <body>. Until the opener itself takes focus, hand it to the visible
  // `Ask why`, and follow the flip when that button unmounts. Following stops as soon as focus is
  // anywhere else.
  useEffect(() => {
    if (chatOpen || focusOpenerOnClose.current === null) return;
    if (focusOpenerOnClose.current === 'follow') {
      const active = document.activeElement;
      if (active !== null && active !== document.body && !active.hasAttribute('data-chat-entry')) {
        focusOpenerOnClose.current = null;
        chatOpenerRef.current = null;
        return;
      }
    }
    const opener = chatOpenerRef.current;
    // `Ask about failures` hides once it has selected a failing run, so fall back to that run's `Ask why`.
    const target = isShown(opener)
      ? opener
      : Array.from(document.querySelectorAll<HTMLElement>('[data-chat-entry]')).find(isShown);
    target?.focus();
    if (target === opener || target === undefined) {
      focusOpenerOnClose.current = null;
      chatOpenerRef.current = null;
    } else {
      focusOpenerOnClose.current = 'follow';
    }
  }, [chatOpen, layout]);
  // The page-level chat entry: shown when the open run has nothing to explain (so it carries no `Ask
  // why`) but a run on the first page does (below). Chat closed, it opens the chat on the latest such run. Chat open,
  // it only moves the selection there: not an open, so it sends no event. A failing run with the chat
  // open needs neither: the open chat is the entry.
  // First page only (Aris, 2026-11-03, ACT2-1402): the button says "latest", and only page 1 holds the
  // newest runs. On a later page it would jump to that page's newest failure, an older one. Not while a
  // page turn is fetching either: `page` is already 1 then, while `runs` still holds the old page's rows.
  const latestFailingRun = pastRuns.page === 1 && !pastRuns.isChangingWindow ? runs.find(runHasFailures) : undefined;
  const showPageChatEntry =
    chatEnabled && latestFailingRun !== undefined && !(selected !== undefined && runHasFailures(selected));
  const pageChatEntry = useCallback(() => {
    if (!latestFailingRun) return;
    select(latestFailingRun.run_id);
    if (chatOpen) {
      // This button leaves once the run is selected, so focus goes to the chat it just moved.
      document.getElementById(ACTIVATION_CHAT_TITLE_ID)?.focus({ preventScroll: true });
      return;
    }
    openChat('ask_failures');
  }, [latestFailingRun, select, chatOpen, openChat]);

  // `?ask=1`: the general chat's hand-off link (`FAILED_RECORDS_HANDOFF_RULE`) asks for the chat to
  // open on the linked run. Read once, when the chat can open, then dropped from the URL (replace) so
  // closing the drawer, a reload or Back does not reopen it. With the flag off it is left alone.
  // `&q=` carries the person's question from the general chat: the drawer puts it in the composer,
  // once, cut to `ACTIVATION_HANDOFF_QUESTION_MAX_CHARS`, and the person sends it (a link's text never goes out in
  // their name on its own). It is dropped from the URL with `ask`.
  // The open is a render-phase state adjustment (React's "adjusting state on a prop change"), not an
  // effect, so the drawer is open on the same commit; the effect only rewrites the URL.
  // A sync that has never run has no run to ask about, and renders `ListEmpty` instead of the table,
  // so there is no chat to show: the link is still read and dropped, and the chat stays closed. (Every
  // activation's `askUrl` carries `ask=1`, including a new one's.)
  // "Never run" is only known once the run list has SETTLED. `hasEverRun` reads the sync row's `lastRun`,
  // which can lag the run list (a first run started elsewhere, a cached `listSyncsForSource`), so while
  // `listSyncRuns` is still loading an empty `runs` is no evidence either way. Deciding on the first
  // render dropped the hand-off on exactly that case (review on #1030). So the decision waits: open as
  // soon as either source shows a run, drop only once the list has answered (rows or an error) with
  // none, and leave `ask`/`q` in the URL until then, since the URL is the question's only copy.
  const [searchParams, setSearchParams] = useSearchParams();
  const askRequested = searchParams.get(ASK_PARAM) === '1';
  const [askHandled, setAskHandled] = useState(false);
  const [handoffQuestion, setHandoffQuestion] = useState<string | null>(null);
  const askHasRun = hasEverRun || runs.length > 0;
  if (chatEnabled && askRequested && !askHandled && (askHasRun || !pastRuns.isLoading)) {
    setAskHandled(true);
    if (askHasRun) {
      setChatOpen(true);
      // A hand-off link followed with the chat already open puts its question in the same chat: not a new open.
      if (!chatOpen) setChatOpening({ source: 'handoff' });
      const question = Array.from((searchParams.get(QUESTION_PARAM) ?? '').trim())
        .slice(0, ACTIVATION_HANDOFF_QUESTION_MAX_CHARS)
        .join('')
        .trim();
      if (question) setHandoffQuestion(question);
    }
  }
  // One event per open, once its segments are known: `availability`'s answer (or its failure, because the composer still
  // works when only that read fails), and on a hand-off, whose page load can beat the run list, the run. A close before
  // then sends it with what is known. The identity check holds it to one: a new run, a layout flip or a re-render changes
  // the deps, not `chatOpening`.
  const sendOpened = (): void => {
    if (!chatOpening || trackedOpening.current === chatOpening) return;
    trackedOpening.current = chatOpening;
    trackClientEvent(ANALYTICS_EVENTS.activationChatOpened, {
      rsConnectionId: syncId,
      source: chatOpening.source,
      ...(linkModel !== null ? { linkModel } : {}),
      ...(perRecordReads !== null ? { perRecordReads } : {}),
      ...(destinationDefinitionName ? { destinationDefinitionName } : {}),
      ...(openingRun.current?.opening === chatOpening && openingRun.current.runFailed !== undefined
        ? { runFailed: openingRun.current.runFailed }
        : {}),
    });
  };
  useEffect(() => {
    sendChatOpened.current = sendOpened;
  });
  // The run the chat opened on, fixed once known, because the event can wait on `availability` while the person picks
  // another run. `Ask about failures` opens on a failing run (the selection moves through the URL, a commit behind this).
  // A hand-off's run is the one its link names, else the list's newest (Rule 1 below), read once the list has stopped
  // fetching: a cached list is on screen and clickable before its refetch can change it, so neither its first row nor a
  // pick during the refetch is that run. A close before then sends none. A ref beside `chatOpening`, not in it: that
  // object's identity is what holds the open to one event.
  const handoffListSettled = !pastRuns.isFetching;
  useEffect(() => {
    if (!chatOpening) return;
    if (openingRun.current?.opening !== chatOpening) {
      openingRun.current = { opening: chatOpening, linkedRunId: selectedRunId, known: false };
    }
    const current = openingRun.current;
    if (current.known) return;
    if (chatOpening.source === 'handoff' && !handoffListSettled) return;
    current.known = true;
    if (chatOpening.source === 'ask_failures') {
      current.runFailed = true;
      return;
    }
    const run =
      chatOpening.source === 'handoff'
        ? current.linkedRunId === null
          ? runs[0]
          : runs.find((r) => r.run_id === current.linkedRunId)
        : selected;
    if (run) current.runFailed = runHasFailures(run);
  }, [chatOpening, handoffListSettled, runs, selected, selectedRunId]);
  const chatOpeningReady =
    chatOpening !== null &&
    (linkModel !== null || chatAvailabilityFailed) &&
    (chatOpening.source !== 'handoff' || handoffListSettled);
  useEffect(() => {
    if (chatOpening && trackedOpening.current !== chatOpening && chatOpenedAt.current === null) {
      chatOpenedAt.current = Date.now();
    }
    if (chatOpeningReady) sendChatOpened.current();
  }, [chatOpening, chatOpeningReady]);
  const clearHandoffQuestion = useCallback(() => setHandoffQuestion(null), []);
  useEffect(() => {
    if (!chatEnabled || !askRequested || !askHandled) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(ASK_PARAM);
        next.delete(QUESTION_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [chatEnabled, askRequested, askHandled, setSearchParams]);

  // "A new run appeared" only means anything against a STABLE query. `usePastRuns` resets to page 1
  // on a range change, so page + range together is the query's identity — a page turn or a range
  // change always introduces ids this panel has never fetched before, and none of them are "new" in
  // the sense Rule 2 cares about; only a REFETCH of the SAME identity returning a different `runs[0]`
  // is. `page`/`range` are read straight off `pastRuns`, not derived from `runs` — but `usePastRuns`
  // sets `placeholderData: keepPreviousData`, so `pastRuns.page` (and this identity) can update ONE
  // RENDER BEFORE `runs` catches up to that new page — `pastRuns.isFetching` is what's still true for
  // that render, which is why the effect below waits for it to clear before touching either ref.
  // Evaluating the identity/`runs` pair while they're out of sync (observed live) re-baselines
  // `knownRunIds` against the OLD page's data under the NEW identity, so the real new page's `runs[0]`
  // then reads as "unseen" on the very next pass and gets selected by Rule 2 regardless of `live`.
  // `syncId` is deliberately NOT in this string. It was, briefly, and that was the wrong half of the
  // fix: React Router does not remount on a params-only change, so navigating from one activation's
  // detail page to another carried EVERY ref across — and `hasDefaulted` is not identity-keyed, so
  // re-baselining alone still left the second sync on "no run selected". `usePastRuns` kept the first
  // sync's page and range too, which no identity string can undo. The panel is keyed on `syncId` by its
  // caller instead (`ActivationDetail`), so a different sync is a different component and all of that
  // state is new by construction. One mechanism, not two.
  const queryIdentity = `${pastRuns.page}|${pastRuns.range.from.getTime()}|${pastRuns.range.to.getTime()}`;
  // Read out of the identity string rather than parsed back out of it — Rule 2 below is gated on it.
  const isFirstPage = pastRuns.page === 1;
  const knownRunIds = useRef<Set<string>>(new Set());
  const baselineIdentity = useRef<string | null>(null);
  const hasDefaulted = useRef(false);
  /**
   * "This reader started a run and has not seen it yet" — an INTENT, latched, not the live prop.
   *
   * `userStartedRun` is only true while the run button is out of `idle`. A run that changes nothing
   * lives about four seconds, so by the time the table's refetch lands the flag is already false and
   * Rule 2 below never fires: the new row appears and the detail pane stays on the previous run.
   * Reading the prop directly made following your own run depend on the run being SLOW.
   *
   * Latched at the start, spent when Rule 2 uses it, and abandoned on a navigation — a page turn or a
   * range change means the reader went looking for something else.
   */
  const followIntent = useRef(false);
  useEffect(() => {
    if (userStartedRun) followIntent.current = true;
  }, [userStartedRun]);

  useEffect(() => {
    // `isFetching` — not `isLoading` — because a page turn re-fetches through `keepPreviousData`,
    // which does NOT flip `isLoading`; `isFetching` is what covers both the first load and a
    // navigation. Waiting for it to clear is what keeps `queryIdentity` and `runs` a matched pair.
    if (runs.length === 0 || pastRuns.isFetching) return;
    const newest = runs[0]!;

    // A navigation — the first load, a page turn, or a range change — always RE-BASELINES rather than
    // comparing against the old set. Seeding is UNCONDITIONAL here, not folded into Rule 1's guard
    // below: a deep link (`selectedRunId` already set from the URL) must seed too, or every run on
    // that first page reads as "never seen" the moment a later poll or navigation revisits it, and
    // Rule 2 would steal a selection the URL — not this panel — chose. (Previously it only seeded
    // inside Rule 1's branch, which Rule 1 skips whenever the URL already names a run — leaving
    // `knownRunIds` empty and making the very next poll's `runs[0]` look "new".)
    if (baselineIdentity.current !== queryIdentity) {
      // Only a real NAVIGATION abandons the follow — a page turn or a range change, where the reader
      // went looking for something else. The FIRST baseline is not one: the panel mounting for the
      // first time is how a reader who just pressed Run sync gets here at all, and clearing the intent
      // on it cancelled the follow before the run they started had appeared.
      if (baselineIdentity.current !== null) followIntent.current = false;
      baselineIdentity.current = queryIdentity;
      knownRunIds.current = new Set(runs.map((run) => run.run_id));

      // Rule 1 — default. Once only, and only when the URL asked for nothing. A URL that names a run
      // wins even if that run is not on THIS page (e.g. an older page) — the caller may still be
      // paging towards it, so this must not steal that in-progress navigation.
      if (!hasDefaulted.current && selectedRunId === null) {
        hasDefaulted.current = true;
        selectSilently(newest.run_id);
      }
      return;
    }

    // Rule 2 — follow a run that did not exist before, ON THIS SAME page/range, only on PAGE 1, and
    // only when THIS reader started it. The header's own poll learns the new run id first and this
    // table follows once ITS poll — a refetch of the SAME query — catches up.
    //
    // `userStartedRun`, not `live`: `live` is also true for a run nobody here asked for, so gating on
    // it let a cron fire drag the pane off whatever was open. For a manual start the two windows are
    // identical (both open at `startSync` and close when the machine parks), so nothing about
    // following your own run changes.
    //
    // The page-1 gate is not belt-and-braces: paging is OFFSET-based (`usePastRuns`, page size 10),
    // so a new run at the head shifts EVERY page down by one. On page 2 a refetch then lands what was
    // page 1's tenth run at `runs[0]`, which page 2's baseline has never held — indistinguishable
    // here from "a new run appeared". The re-baseline branch above does not cover it: it fires on a
    // page TURN (the identity changed), not on a refetch of the SAME page whose contents shifted
    // underneath it. Only page 1 can hold the newest run, so only page 1 may follow one — and since
    // this REPLACES, an ungated version destroyed the history entry for the run being read as well.
    //
    // What the branch above DOES guarantee is narrower than it used to claim: a navigation (first
    // load, page turn, range change) re-baselines instead of comparing, so a different `runs[0]`
    // arriving WITH a new identity is never "new". A different `runs[0]` under the SAME identity is
    // what this branch judges, and the gates below are the whole of what bounds it.
    const isNew = !knownRunIds.current.has(newest.run_id);
    runs.forEach((run) => knownRunIds.current.add(run.run_id));
    if (isNew && followIntent.current && isFirstPage) {
      followIntent.current = false;
      selectSilently(newest.run_id);
    }
  }, [runs, queryIdentity, pastRuns.isFetching, selectedRunId, selectSilently, isFirstPage]);

  // Memoised, and the dependency list is honestly EMPTY: every value the three cells below close
  // over is a module import, and the one that was not — the ticking `now` — moved into
  // `RunDurationCell` above precisely so this array could hold still. See that component's doc for
  // what a per-render columns array costs (`Table` rebuilds every columnDef, `flexRender` remounts
  // every cell subtree). If a future column needs a value from this render, it belongs in a leaf
  // component like `RunDurationCell`, not in this closure — adding it to the dep list here would
  // reinstate the remount rather than fix it.
  const columns = useMemo<TableColumn<RawSyncRun>[]>(
    () => [
      {
        id: 'run',
        header: 'Run',
        activatesRow: true,
        activatesRowLabel: (run) => `Open run from ${runStartedLabel(run)}`,
        // All-phrasing content: under `activatesRow` this cell renders inside a `<button>`, which
        // permits no `<div>` and no second focusable element — hence `title` rather than a tooltip
        // component, which would plant a `tabIndex={0}` span inside the row activator.
        //
        // NO TRIGGER GLYPH. This cell used to lead with a clock for a manual run and a repeat arrow
        // for a scheduled one. Both are gone, which finishes the job `ScheduleLabel.tsx` started —
        // read its doc comment for the reasoning (the activations list table carried the identical
        // pair, and the clock was actively WRONG: a manual sync is the one with NO schedule).
        //
        // The honest difference, stated so nobody has to re-derive it: in that table the glyph was
        // redundant with adjacent words, and HERE IT IS NOT. This meta line renders the status badge,
        // `runTypeLabel` (Full/Incremental) and `runSummary`; the trigger appears nowhere in words. So
        // this removal does drop a fact from the row. It is still right, for three reasons:
        //
        //   · The glyph was `aria-hidden`, so the trigger NEVER reached assistive technology. Removing
        //     it levels sighted and unaided readers rather than making either worse off.
        //   · The run detail card states it in words — `Scheduled · Incremental`, `RunIdentityCard`'s
        //     `provenance` — whenever a run is selected, which is one click away and is where the rest
        //     of a run's provenance already lives.
        //   · The meta line is already at its budget: `runSummary` below is `min-w-0 truncate` and
        //     truncates today. A text token here would push the summary out to restate what the card
        //     carries.
        //
        // A TEXT SUBSTITUTE WAS CONSIDERED AND REJECTED for that third reason. This is a removal, not
        // a swap — do not reinstate either the glyph or a "Manual"/"Scheduled" token in this cell.
        cell: (run) => (
          <span className="block min-w-0">
            <span className="flex items-center gap-1.5">
              <span
                className="truncate text-sm font-medium text-foreground"
                title={formatDateTimeTitle(run.started_at)}
              >
                {runStartedLabel(run)}
              </span>
            </span>
            {/* Wraps only when the badge and the type label cannot share a line (a narrow split, a
                phone): the summary is `flex-1 basis-0`, so it never pushes itself onto a line of its own
                — it truncates beside them as long as 3rem of it fits. */}
            <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              {/* `bordered`: this table's selected row is `bg-muted`, which is the same token as the
                  Running badge's own fill (`--info-bg`), so without a border the badge disappears into
                  the row it is sitting on. Scoped here rather than forced inside the badge — see its
                  own comment. */}
              {/* `max-w-full whitespace-normal`: in the narrowest step (a phone, the runs at their 38%
                  minimum) the cell can be narrower than "Completed with failures", and the badge then
                  wraps inside itself rather than being cut off. It never wraps while it fits. */}
              <SyncStatusBadge run={run} bordered className="max-w-full justify-start text-left whitespace-normal" />
              <span className="shrink-0 text-xs text-muted-foreground">{runTypeLabel(run)}</span>
              {/* Truncated, and `title` repeats the same humanized text (not an exact-figure
                  fallback) so the line stays reachable via native tooltip / a11y tree when it
                  truncates. The exact figures are not surfaced anywhere in this cell. */}
              <span className="min-w-12 flex-1 basis-0 truncate text-xs text-muted-foreground" title={runSummary(run)}>
                {runSummary(run)}
              </span>
            </span>
          </span>
        ),
      },
      {
        id: 'delta',
        header: 'Changed → Accepted',
        // 15rem (240px), not 12rem (192px). At 12rem this cell OVERLAPPED the Duration column: the
        // `td` is `whitespace-nowrap` with `overflow-x: visible`, so a string wider than the declared
        // width is neither wrapped nor clipped — it paints over its neighbour. Measured at 1280x800,
        // 12px Inter: the everyday full-house row ("1,230 of 1,230 changed · 100.0%") needs 209px
        // including the cell's `px-3`, and `liveSendingMidFlight`'s "162,380 of 400,000 changed ·
        // 40.6%" needs 237px. Both now fit.
        //
        // Width alone cannot fix this and must not be treated as the fix — an audience of ten million
        // needs 272px, and there is no width that is safe for every account. `min-w-0 line-clamp-2` below
        // is what makes the overlap structurally impossible; the width only decides how rare a wrap
        // is. This is the same `min-width: auto` mechanism the detail column's own comment
        // explains further down this file, one level in: it was applied to the pane and not to the
        // cell.
        //
        // The width is a step, not a constant (`RUN_COLUMN_WIDTHS`): 15rem from 46rem of table up, less
        // below. A figure wider than its step WRAPS to a second line (the row already has two), and
        // `line-clamp-2` keeps the overlap guarantee `truncate` used to give.
        width: 'var(--runs-col-delta)',
        cell: (run) => {
          const changed = group(run.metrics?.changed);
          const successful = group(run.metrics?.successful);
          const outcome = deliveryOutcome(run);
          return (
            <div className="flex min-w-0 items-center gap-1.5 text-xs">
              {/* Denominated, not a bare pair: `1,000 → 0` states neither what 1,000 is of nor how far
                short 0 fell. `deliveryOutcome` — not the raw comparison, and not the collapsed
                two-way boolean this cell used to key off of (since deleted — it could not tell a
                `refused` run from an `abandoned` one) — because this cell sits in the same row as
                `SyncStatusBadge`: a `refused` run's badge reads warning, so this number has to match
                it rather than blanket-painting every unresolved gap danger (the exact defect
                the retired `TwoSidedDelta` drill-in was fixed for). Only `abandoned` — a gap with no owner —
                earns danger; a run still in flight (`pending`) gets neither, because it has
                delivered nothing YET, which is not the same as having failed to.

                `counted` is not optional here even though the prop has a default: without it this
                cell read "0 of 0 changed" for a run whose sub-line, in the SAME ROW, said "Counting
                records." — the row contradicting itself rather than merely overclaiming. Every metric
                is 0 during the extract half (`liveExtract`, observed) and the same run settles at
                1,230. */}
              <RunDenominated
                n={successful.total}
                d={changed.total}
                unit="changed"
                counted={isCounted(run)}
                className={cn(
                  // Both halves are load-bearing: a clamp alone does nothing to a flex item whose
                  // `min-width` still resolves to `auto` (its content's minimum), which is exactly how
                  // this cell came to paint over Duration. Two lines, not one: the row already has two,
                  // so a figure too long for the column shows whole on a second line before it ever
                  // reaches an ellipsis.
                  'line-clamp-2 min-w-0 whitespace-normal',
                  outcome === 'abandoned' && 'text-danger',
                  outcome === 'refused' && 'text-warning',
                )}
              />
            </div>
          );
        },
      },
      {
        id: 'duration',
        header: 'Duration',
        width: 'var(--runs-col-duration)',
        cell: (run) => <RunDurationCell run={run} />,
      },
    ],
    [],
  );

  // A REFRESHED-EVERY-COMMIT ref for `select` — not the ref-latch this file rejected before. That
  // earlier rejection was about a ref set ONCE (`useRef(select)` with nothing to keep it current):
  // such a ref keeps handing every later click the render-1 `setSearchParams`, and its own captured
  // `searchParams` snapshot is exactly what a functional update merges into — so a click after the
  // URL had already changed once would silently write back over a stale snapshot. That reasoning is
  // correct, and still applies to that construction. It does not apply here: the effect below has NO
  // dependency array, so it reruns after every commit and `selectRef.current` is never more than one
  // commit old. A click is a discrete event, not a render-phase read — by the time it fires, React has
  // already committed and run effects for whatever render produced the button the reader is looking
  // at — so `selectRef.current` at click time is THAT render's `select`, byte-for-byte what closing
  // over `select` directly would have used. Verified against the installed `react-router@7.17.0`
  // (`chunk-6CSD65Y2.mjs:10774-10782`): `setSearchParams` is `useCallback(fn, [navigate, searchParams])`,
  // whose functional-update form receives `new URLSearchParams(searchParams)` captured at THAT
  // callback's own render — so the freshest `select` the ref can hold is also the freshest captured
  // `searchParams`, never a stale one.
  const selectRef = useRef(select);
  useEffect(() => {
    selectRef.current = select;
  });

  // The other half of `Table`'s `[columns, onRowClick]` memo — now genuinely, permanently stable.
  // Closing over `select` directly (the previous version of this callback) meant its identity tracked
  // react-router's `setSearchParams`, which v7 rebuilds on every `location.search` change — so every
  // SELECTION CHANGE, including this panel's own auto-select at mount, rebuilt `columnDefs` and
  // remounted every cell, dropping a keyboard user's focus to `<body>` on the very click that
  // activates a row. Reading `select` through `selectRef` above instead removes that last residual:
  // this callback's identity never changes across the panel's life, so a selecting click no longer
  // remounts anything.
  // No disable needed here: `select` is read through `selectRef.current`, not closed over directly,
  // so `react-hooks/exhaustive-deps` has nothing to flag against the empty array — it lints the
  // identifiers this closure actually references, and `selectRef` (stable, from `useRef`) is the only
  // one. See `selectRef`'s comment above for why an empty dep array is safe here regardless.
  const handleRowClick = useCallback((run: RawSyncRun) => {
    // A user's click — pushes history, so Back returns to whatever was selected before.
    selectRef.current(run.run_id);
  }, []);

  return (
    // The toolbar sits ABOVE the table, in the table's own grid column rather than spanning the row —
    // so it reads as the table's toolbar, not a third element the two panes have to align against, and
    // the table and the run detail share one top edge (both start row 2). See `RUNS_GRID`. The panel is
    // the container that grid measures. A fixed top-margin on the detail column was rejected for the
    // reason ui/AGENTS.md's Tailwind section gives generally — it would hardcode this toolbar's height.
    <div ref={panelRef} className="flex min-h-0 flex-1 flex-col">
      <div
        className={cn(
          RUNS_GRID.base,
          layout === 'beside'
            ? RUNS_GRID.beside
            : !showStrip
              ? RUNS_GRID.withoutDetail
              : drawerShown
                ? RUNS_GRID.withStrip
                : RUNS_GRID.withStripPageScroll,
        )}
      >
        {/* The open run, as a strip above the toolbar, wherever the detail column is not beside the
            table — the same run the column shows, from the same components in their horizontal
            layouts. `Ask why` rides on the strip while the chat is closed, as it does on the column. */}
        {showStrip && selected ? (
          <RunStrip
            run={selected}
            destinationDefinitionName={destinationDefinitionName}
            warehouseName={sourceDefinitionName}
            syncMode={syncMode}
            retentionDays={retentionDays}
            identifierMappings={identifierMappings}
            fieldMappings={fieldMappings}
            fieldMappingsLabel={fieldMappingsLabel}
            onEditConnection={onEditConnection}
            editConnectionDisabledReason={editConnectionDisabledReason}
            onAskWhy={chatEnabled && !drawerShown ? askWhy : undefined}
            className="col-span-full min-w-0"
          />
        ) : null}
        {/* `Sync runs`, and the pager on the same line — the frame's toolbar (`334:524`). The two
          controls that bound what the table shows (which WINDOW, which PAGE) now sit on one row with
          the title, which is what freed the row of vertical space the pager used to own below the
          table. `flex-wrap`: in a narrow pane the pager drops to a second line rather than past the
          pane's edge. */}
        <div className="col-start-1 flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="text-[0.8125rem] leading-[1.125rem] font-semibold text-foreground">Sync runs</h2>
          {/* The PICKER gets the untouched prop, not `windowDays`. `null` is the connection's own "Do
            not retain logs" choice, and the control is where that has to show: the query fallback
            below is a query detail, but offering "Last 30 days" on a connection that keeps nothing
            would be this control contradicting the band that states the same fact. */}
          <PastRunsRangePicker
            range={pastRuns.range}
            onChange={pastRuns.setRange}
            disabled={pastRuns.isChangingWindow}
            retentionDays={retentionDays}
          />
          <div className="ml-auto flex items-center gap-2">
            {showPageChatEntry ? (
              <Button
                variant="secondary"
                size="sm"
                Icon={<MessageSquareText aria-hidden />}
                onClick={pageChatEntry}
                data-chat-entry=""
              >
                {drawerShown ? 'Go to latest failure' : 'Ask about failures'}
              </Button>
            ) : null}
            {/* No Refresh control, matching the frame. There was one, justified by a settled table that
              never refetched; `pastRunsPollIntervalMs` now returns a slow cadence instead of `false`
              (`usePastRuns.ts`), so the stale-list case it existed for cannot arise. It was also a
              promise this query cannot keep: it re-asks for the CURRENT offset and window, so on
              page 2 it shifts rows down rather than surfacing the new run at the head of page 1, and
              in a window that excludes today it can never surface one at all. */}
            {/* Gated on pageCount, not on the loaded rows: a page that came back empty because the
              result set shrank under the user is exactly when they need the control to get back.
              Below that threshold there is one page, both chevrons would be inert, and `10 of 10
              runs` restates the table — so the whole group goes rather than shipping a dead control. */}
            {pastRuns.pageCount > 1 ? (
              <PastRunsPagination
                page={pastRuns.page}
                pageCount={pastRuns.pageCount}
                totalRuns={pastRuns.totalRuns}
                loadedRuns={runs.length}
                onPageChange={pastRuns.setPage}
                disabled={pastRuns.isChangingWindow}
              />
            ) : null}
          </div>
        </div>

        {!hasEverRun && runs.length === 0 ? (
          // `runs.length === 0` as well as `!hasEverRun`, and the second half is not belt-and-braces.
          // `hasEverRun` comes from `listSyncsForSource`; the rows come from `listSyncRuns`. Nothing
          // invalidates the first for a run started ELSEWHERE — a schedule, another tab, the RudderStack
          // webapp — so on a sync that had never run, the poll can deliver that run's row here while
          // `lastRun` is still missing. Gating on the flag alone showed "No runs yet" over a table that
          // had the run in hand, and did not recover until reload. Rows win: if there is something to
          // show, show it.
          //
          // A never-run sync has no rows to head and no run to select, so the table frame and the
          // detail column both COLLAPSE — there is nothing for either to be beside. One `ListEmpty`
          // fills the whole content region instead of two identical ones sitting side by side, neither
          // of them centred on the page. `ListEmpty` already renders `flex-1 items-center justify-center`
          // (`ListStates.tsx`); its wrapper spans every grid column, so it centres across the FULL content
          // region — not the table's column.
          //
          // The toolbar above is NOT part of this collapse — it stays. The range picker in it is what
          // decides whether runs show up at all; a reader who suspects a narrow range is why this reads
          // empty still needs the control that would prove (or fix) it. See the `hasEverRun` prop's doc
          // comment for the fuller reasoning, and the file doc comment's retention paragraph for why the
          // toolbar's OTHER stated fact (retention) still says nothing here either.
          <div className="col-span-full flex min-h-0 flex-col">
            <ListEmpty icon={History} title="No runs yet" description="This sync has not run yet." />
          </div>
        ) : (
          /* Two panes, NEVER a toggle between them — see the file doc comment. Both are grid items of
           `RUNS_GRID`. Side by side (and beside the chat) the grid is `min-h-0 flex-1`, so its second row is
           a definite height, and `Table`'s `fillHeight` below depends on that.

           Nothing here scrolls sideways any more. The row used to be two FIXED columns (46.5rem + 23rem
           + gap = 71rem) wider than the content region at 1280×800, so it scrolled horizontally, and
           with the failure chat open the table alone (46.5rem) was wider than the runs pane, so its
           last column and the pager sat under the pane's edge. The table is fluid now, and when there is no
           room for both the run goes above it as the strip (`runDetailLayout`). */
          <>
            {/* The table's column: fluid (`min-w-0`), and the container its column widths step on
              (`RUN_COLUMN_WIDTHS`). */}
            <div className="@container col-start-1 flex min-h-0 min-w-0 flex-col">
              {/* The height-bearing region: `min-h-0 flex-1` so IT is what shrinks/grows to fill the
                column, handing `Table`'s `fillHeight` a definite height regardless of which of the
                three branches below renders. */}
              <div className={cn('min-h-0 flex-1', RUN_COLUMN_WIDTHS)}>
                {/* `error && runs.length === 0` deliberately: a failed background refetch must not blank a
                  table that is still showing good cached data. */}
                {pastRuns.error && runs.length === 0 ? (
                  <ListError
                    title="Could not load runs"
                    message="The run history request failed. This says nothing about the sync itself — it may be running right now."
                  >
                    <Button variant="secondary" onClick={pastRuns.refetch}>
                      Retry
                    </Button>
                  </ListError>
                ) : pastRuns.isLoading || (runs.length === 0 && (live || userStartedRun)) ? (
                  // The inverse of the gate above. `hasEverRun` can flip TRUE before this table's own
                  // refetch lands — this reader starts a first run, `listSyncsForSource` invalidates, and
                  // `lastRun` arrives first. For that gap the two-pane body is rendering against an empty
                  // `runs`, and the table's own empty state would announce "No runs in this window" about
                  // a run that is at this moment starting. A run in flight is a reason to wait, not a
                  // reason to describe the window.
                  <ListSkeleton view="list" />
                ) : (
                  <Table
                    data={runs}
                    columns={columns}
                    getRowId={(run) => run.run_id}
                    onRowClick={handleRowClick}
                    selectedRowId={selectedRunId}
                    fixedLayout
                    fillHeight
                    // Headers may wrap, and cells pad less in the narrowest step — see `RUN_COLUMN_WIDTHS`.
                    className="[&_td]:px-2 @lg:[&_td]:px-3 [&_th]:px-2 [&_th]:whitespace-normal @lg:[&_th]:px-3"
                    // Two-line rows measure ~58px in Band B, not the primitive's single-line 33px default.
                    // Only load-bearing above AUTO_VIRTUALIZE_THRESHOLD (100), which a 10-row page never
                    // reaches — but the default is wrong and a page-size change should not silently break
                    // virtualization.
                    estimateRowHeight={58}
                    emptyState={
                      // The retention rule is on the header line directly above and still renders here, so
                      // this line does not repeat it.
                      //
                      // One fact is reachable here, but it takes two guards rather than one to get that
                      // way. The branch above collapses a sync with no runs AND no rows, and the loading
                      // branch holds this back while a run is in flight — so what is left really is a sync
                      // that HAS run with nothing in THIS particular window. Widening the window, or
                      // picking a different one, is exactly the right move here.
                      <ListEmpty
                        icon={History}
                        title="No runs in this window"
                        description="Every run falls outside this window. Widen it or pick a different range."
                      />
                    }
                  />
                )}
              </div>
            </div>

            {/* 23rem = Band B's 368px. The detail NEVER displaces the table: a failure state with a long
              error string wraps and scrolls inside this column rather than pushing the run list off
              screen, which is the exact defect this layout exists to fix. Only ever shown beside the
              table (`runDetailLayout`); every other layout shows the strip above the table instead.

              `overflow-y-auto` is what makes that a guarantee rather than a hope, and the coupling is not
              obvious: a flex item's `min-width` resolves to `auto`, which is its CONTENT's minimum — an
              unbreakable token would then push this item past its declared `w-[23rem]` and squeeze the
              table. A scroll container is exempt: `overflow` other than `visible` makes `min-width:auto`
              resolve to 0. So "let the whole page scroll instead" would silently destroy the guarantee
              this comment claims. Keep the scroll on THIS column; `break-words` on the error string
              (`RunOverview`, review IMPORTANT 4) is the other half — it keeps the content readable rather
              than merely contained. */}
            {/* The detail column's own frame. HIDDEN in the strip layout: while the failure chat is open
              (spec decision 8) the chat takes the page's right-hand pane and stands in for it, and in a
              pane too narrow for it the strip above the table does. `hidden`, not unmounted, so the
              detail keeps its scroll and its `Ask why` (the focus target when a chat opened from it
              closes) stays in the document. */}
            <div
              className={cn(
                'flex min-h-0 min-w-0 flex-col',
                layout === 'beside' ? 'col-start-2 row-start-2' : 'hidden',
              )}
            >
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {/* Review IMPORTANT 2: "No run selected / Pick a run to see what it did." used to render in
                FOUR situations, and is honest in exactly one of them.
                  (a) initial load, while the left column is still a skeleton — there is nothing to pick
                      yet, so an instruction to pick is a lie about the state of the page, not the run.
                  (b) the run list FAILED with nothing cached — the left column is showing "Could not load
                      runs" while this one tells the reader to pick from it. This case is not `isLoading`:
                      TanStack v5's `isLoading` is `isPending && isFetching`, and a settled error is
                      neither, so it fell through to (c) — the branch whose whole claim is that runs are
                      there to be picked. Gated on the same `error && runs.length === 0` pair the left
                      column uses, so a failed BACKGROUND refetch (cached rows still on screen, still
                      pickable) keeps the honest copy.
                  (c) genuine no-selection with runs present — the only case the instruction is honest in,
                      e.g. a URL-selected run that isn't on the currently loaded page.
                A never-run sync is no longer a case HERE at all — it collapses the whole two-pane body
                one level up (the `!hasEverRun` branch above), so this column, like the table beside it,
                only ever renders once `hasEverRun` is known true.
                (a) and (b) mirror the left column's own gate for the same state — (a) its loading gate,
                (b) its `ListError` gate — so the two columns can never describe the page differently.
                The ORDER is not the same: the left column checks `error` before `isLoading` (below), this
                one checks `isLoading` before `error`. That is inert, not a second bug to fix — TanStack
                v5's `isLoading` is `isPending && isFetching`, and a settled `error` is never `isPending`,
                so the two gates are mutually exclusive and which one is checked first cannot change which
                branch renders. */}
                {selected ? (
                  <RunOverview
                    run={selected}
                    destinationDefinitionName={destinationDefinitionName}
                    warehouseName={sourceDefinitionName}
                    syncMode={syncMode}
                    // Untouched, NOT `windowDays`: `null` is retention turned off and the band states that as a
                    // state. The query's fallback stops here.
                    retentionDays={retentionDays}
                    identifierMappings={identifierMappings}
                    fieldMappings={fieldMappings}
                    fieldMappingsLabel={fieldMappingsLabel}
                    onEditConnection={onEditConnection}
                    editConnectionDisabledReason={editConnectionDisabledReason}
                    onAskWhy={chatEnabled ? askWhy : undefined}
                  />
                ) : pastRuns.isLoading ? (
                  <RunDetailSkeleton />
                ) : pastRuns.error && runs.length === 0 ? (
                  // No Retry of its own: the left column's own `ListError` — on screen at the same time,
                  // an inch away — already carries one, and a second button retrying the same query would be
                  // two controls for one action.
                  <ListError
                    title="No run to show"
                    message="The run history request failed, so there is nothing to open. Retry from the list beside this."
                  />
                ) : (
                  <ListEmpty icon={History} title="No run selected" description="Pick a run to see what it did." />
                )}
              </div>
            </div>
            {ChatDrawer && chatPane
              ? createPortal(
                  <ChatDrawer
                    syncId={syncId}
                    selectedRunId={selectedRunId}
                    selectedRunLabel={selected ? runStartedLabel(selected) : null}
                    selectedRunFailed={selectedRunFailedCount(selected)}
                    selectedRunHasFailures={selected !== undefined && runHasFailures(selected)}
                    selectedRunRunning={isRunning(selected)}
                    activationLabel={activationLabel}
                    failuresRetentionDays={failuresRetentionDays}
                    pageRuns={runs}
                    initialQuestion={handoffQuestion}
                    onInitialQuestionPlaced={clearHandoffQuestion}
                    open={drawerShown}
                    onClose={closeChat}
                    perRecordReads={perRecordReads}
                    linkModel={linkModel}
                  />,
                  chatPane,
                )
              : null}
          </>
        )}
      </div>
    </div>
  );
}
